import HELPER from '@/app/utils/HELPER';
import type { AtomModel, FormControlSetting, TabSettingsData, TypeSettings } from '@/app/editor/iframe/atom/Atom.model';
import { AtomGroup } from '@/app/editor/iframe/atom/Atom.model';
import { getModuleData } from '@/app/editor/sidebar/utils/getModuleData';
import moduleInfo from '@/app/editor/sidebar/module/register';

import type { Observer, Subject } from '@/app/editor/iframe/atom/service/observer';
import { DataManager } from '@/app/editor/iframe/atom/service/DataManager';
import { SettingHandlerManager } from '@/app/editor/sidebar/settings/setting-handler/BaseSettingHandler';
import snippetSettingLinks from '@/app/editor/sidebar/utils/gfSnippetSettingLinks';
import { base } from '@/app/editor/sidebar/constant';
import './setting-handler';
import { app } from '@/store';

type ShadowValue = {
  color: string;
  y: string;
  x: string;
  blur: string;
};

/**
 * getRefreshType
 * @param setting ..
 * @param defaultValue ..
 * @returns refreshType
 */
function getRefreshType(setting: FormControlSetting, defaultValue: string): string {
  if (setting.refreshType) {
    return setting.refreshType;
  }
  return defaultValue;
}

// const standardtitleTags = ["h1", "h2","h3", "h4","h5", "h6","p"];
/**
 * Khởi tạo panel settings bên leftsidebar
 * Render các controls bên trong md-body (Lấy dữ liệu từ bkContent)
 * - Render các control tab settings
 * - Render các control tab design
 * Khởi tạo tab settings và design
 * Lắng nghe sự thay đổi của các controls và update vào bkContent
 */
export class Settings implements Observer {
  public $el: JQuery;

  /* Editor */
  private $gfLeftSideBar: JQuery = jQuery('#gfLeftSideBar');
  private $gfResTool: JQuery = jQuery('#gfResTool');

  /* Iframe */
  private $gfFrame: JQuery = jQuery('#gfFrame');
  private $gryffEditor: JQuery = this.$gfFrame.contents().find('.gryffeditor').first();
  private currentAtomId: string;
  public $divContentBk: JQuery = this.$gfFrame.contents().find('#divContentBk');
  /** constructor
   * @param element DOM
   * @param options Seting
   */
  constructor(element: HTMLElement, options?: TypeSettings) {
    // init everthing
    this.$el = jQuery(element); // merge element to $el
    this.currentAtomId = this.$el.attr('data-id') || '';
    DataManager.registerObserver(this);
  }

  /**
   * update data change
   * @param subject obs
   */
  update(subject: Subject): void {
    if (DataManager.getState('openSetting', this.currentAtomId)) {
      DataManager.setState('openSetting', false, this.currentAtomId);
      const data: TabSettingsData = DataManager.getDataSetting(this.currentAtomId);
      this.openSetting(data);
    }
  }

  /**
   * cache function
   * @param data TabSettingsData
   */
  openSetting(data: TabSettingsData): void {
    this.open(data.$atom, data.atom, data.tab, data.drag, data.capture, data.forceOpen);
  }

  /**
   * get $target for GfTabs
   * @param $atom $atom
   * @param model model
   * @returns $target for GfTabs
   */
  get$targetForGfTab($atom: JQuery, model: AtomModel): JQuery {
    const atomGroup = $atom.attr('data-atomgroup') as AtomGroup;

    if (atomGroup === AtomGroup.ChildProduct && model.class != 'module-wrap') {
      return $atom.find(model.class);
    }

    return $atom;
  }

  /**
   * open tab và focus vào tab setting
   * @param $atom DOM atom
   * @param atomModel atom data
   * @param tab tab name (Settings)
   * @param drag drag or click
   * @param capture save history
   * @param forceOpen force Open
   */
  open($atom: JQuery, atomModel: AtomModel, tab = 'Settings', drag = false, capture = true, forceOpen?: boolean): void {
    const _this = this;
    // Đếm số lượt mở open setting để cảnh báo đến người dùng.
    if (drag == undefined) {
      drag = false;
    }
    const type = atomModel.type;
    let $hightlight = jQuery('<div class="gf_panel-hightlight-all module-panel"></div>');
    switch (type) {
      case 'module':
        $hightlight = jQuery('<div class="gf_panel-hightlight-all module-panel"></div>');
        break;
      case 'element':
        $hightlight = jQuery('<div class="gf_panel-hightlight-all element-panel"></div>');
        break;
      case 'row':
        $hightlight = jQuery('<div class="gf_panel-hightlight-all row-panel"></div>');
        break;
    }
    if (!drag) {
      const _countHighlight = this.highlightCounter(atomModel.id, atomModel.type);
      if (_countHighlight >= 3 && _countHighlight % 5 === 0) {
        //   const $hightlight = jQuery("<div class=\"gf_panel-hightlight-all module-panel\"></div>");
        this.$gfLeftSideBar.find('.side-panels').append($hightlight);
        setTimeout(function () {
          $hightlight.remove();
        }, 1000);
      }
    }
    /** Khởi tạo template và render tab setting */
    // avoid 3 times open
    const $mdSettings = this.$gfLeftSideBar.find('#md-settings');
    const currentTab = $mdSettings.find('.simple-tabs ul.simple-tabs-item li.active a').text().trim();
    const merId = $mdSettings.data('edittingID');
    const pasteCSSid = localStorage.getItem('pasteCSS');
    const isPasting = pasteCSSid == merId;
    if (!forceOpen) {
      if ($mdSettings.hasClass('active') && merId == $atom.attr('id') && currentTab == tab && !isPasting) {
        return;
      }
    }

    const mdHtml = this.getTemplatePanelSetting(atomModel);
    const typeEditting = atomModel.type || 'element';
    const nodeId = atomModel.nodeId.replace(/#/g, '');
    const $activePanel = this.showMDSettingsPanel(typeEditting, mdHtml, nodeId);
    const $settingsPanel: JQuery = this.$gfLeftSideBar.find('#md-link .md-body.settings-panel');
    ($settingsPanel as any).gfFormRender({
      fields: atomModel.settings,
    });

    if ($settingsPanel.find('.repeator').length > 0) {
      const $dataHolder = $activePanel.find('.txtDataHidden');
      try {
        const jsonHtml = getModuleData(atomModel.id);
        $dataHolder.text(jsonHtml);
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log(err.message);
      }
      ($settingsPanel as any).find('.repeator').gfRepeator({
        targetElement: this.$el,
        dataHolder: $dataHolder,
        /**
         * Xác định form control thay đổi
         * @param $input DOM
         */
        detectChange: function ($input: JQuery): void {
          if ($input) {
            const name = $input.attr('name');
            let value: any = $input.attr('value');
            if (!value) {
              const val = $input.val();
              if (val) {
                value = val;
              }
            }
            if (name) {
              _this.saveFormControls(name, value, $atom, atomModel, $activePanel);
            }
          }
        },
      });
    }
    // reload Tabs
    const $gfTabs = $activePanel.find('.simple-tabs');
    ($gfTabs as any).gfTabs({
      targetElement: this.get$targetForGfTab($atom, atomModel),
      /**
       * onSettingsChanged
       * @param $input DOM
       * @returns void
       */
      onSettingsChanged: function ($input: JQuery, type?: string): void {
        const name = $input.attr('name');
        let value: any = $input.attr('value');
        if (!value) {
          const val = $input.val();
          if (val) {
            value = val;
          }
        }
        if (name) {
          _this.saveFormControls(name, value, $atom, atomModel, $activePanel, type);
        }
      },
      /**
       * onTabClick
       * @param tabName ..
       * @returns void
       */
      onTabClick: function (tabName: string): void {
        switch (tabName) {
          case 'Settings': {
            const gfResTool = _this.$gfResTool.data('gfrestool');
            const screenType = gfResTool != undefined ? gfResTool.getCurrentStatus() : 'lg';

            // Support feature control field-visible
            $activePanel.find('.columns.field-hide-all').hide();
            $activePanel.find('.columns.field-visible-' + screenType).show();

            _this.readDataFormControls($atom, atomModel, $activePanel);

            // Áp dụng cho Atom Heading, do case này hơi đặc biệt 1 chút
            // Sau khi saveFormControls xong cần phải reaDataFormControl
            // Sẽ tối ưu phần này sau
            setTimeout(function () {
              $activePanel.find('select[name="titleTag"]').on('input-change', function () {
                _this.readDataFormControls($atom, atomModel, $activePanel);
              });
            }, 10);

            break;
          }

          case 'Design': {
            if ($gfTabs.data('gftabs') != undefined) {
              $gfTabs.data('gftabs').onDesignOptionsTabClick();
            }
            break;
          }
        }

        // capture history setting
        setTimeout(function () {
          // if (capture != false && _this.$gryffEditor.data("gfhistory")) {
          if (capture != false) {
            _this.$gryffEditor.data('gfhistory').captureMERSettings();
          }
        }, 100);
      },
    });

    if (tab && tab == 'Design') {
      $gfTabs.data('gftabs').gotoTab(1);
    }
    localStorage.removeItem('pasteCSS');

    const fields = DataManager.getAtomDataById(atomModel.id).settings;
    snippetSettingLinks(fields);
    return;
  }

  /**
   * Read data from all control in tab Settings
   * @param $atom Atom DOM
   * @param atomModel atom data
   * @param $activePanel ActivePanel DOM
   */
  readDataFormControls($atom: JQuery, atomModel: AtomModel, $activePanel: JQuery): void {
    const _this = this;
    const gfResTool = _this.$gfResTool.data('gfrestool');
    const screenType = gfResTool != undefined ? gfResTool.getCurrentStatus() : 'lg';
    const $settingsPanel = $activePanel.find('.settings-panel');
    const formControlSettings = atomModel.formControlSettings;
    const $currentAtom = $atom; // lưu lại biến atom phục vụ cho việc set atom bên dưới
    const atomName = $atom.data('label');
    if (formControlSettings && formControlSettings.length > 0) {
      const style = atomModel.style;
      const mapValue = {};
      formControlSettings.forEach((setting) => {
        $atom = $currentAtom;
        if (setting.selfSelector) {
          const selfSelector: string = setting.selfSelector;
          const isUsingSelf: any = selfSelector.split(',')[0];
          if (isUsingSelf == 1) {
            const moduleClass = this.getTargetClass(atomName);
            if (moduleClass != '') {
              $atom = $currentAtom.find(moduleClass);
            }
          }
        }
        if (setting.attrStoredSelector && setting.attrStoredSelector == '.module') {
          let targetSelector = setting.targetSelector as string;
          if (targetSelector.includes(':hover')) {
            targetSelector = targetSelector.split(':hover')[0];
          }
        }
        switch (setting.type) {
          case 'html:replace': {
            if (!setting.targetSelector) {
              alert(`Thiếu targetSelector: ${setting.name}`);
            } else if (!setting.replaceData) {
              alert(`Thiếu replaceData: ${setting.name}`);
            } else {
              const $target = $atom.find(setting.targetSelector);
              const replaceData = setting.replaceData || {};
              const replaceType = replaceData.replaceType || '';
              const $replaceTarget = $target.find(replaceData.replaceTarget);
              let value = $replaceTarget.html();
              if (replaceType == 'icon') {
                const $icon = $target.find(replaceData.replaceTarget);
                const icon = jQuery('<div/>').append($icon.clone()).html();
                value = HELPER.base64UrlEncode(icon);
              }
              Object(mapValue)[setting.name] = value;
              $settingsPanel.find(`[name="${setting.name}"]`).attr('value', value).val(value).trigger('value-change');
            }
            break;
          }
          case 'html:select': {
            if (!setting.targetSelector) {
              alert(`Thiếu targetSelector: ${setting.name}`);
            } else {
              const $target = $atom.find(setting.targetSelector);
              let options: any = [];
              let value = '';
              try {
                $target.find('option').each(function () {
                  value = jQuery(this).attr('value') == null ? '' : jQuery(this).attr('value') || '';
                  const text: string = jQuery(this).text() ? jQuery(this).text() : '';
                  options.push(value.trim() + ' => ' + text.trim());
                });
                options = options.join('\n');
              } catch (e) {
                console.log(e);
              }
              Object(mapValue)[setting.name] = options;
              $settingsPanel
                .find(`[name="${setting.name}"]`)
                .attr('value', options)
                .val(options)
                .trigger('value-change');
            }
            break;
          }
          case 'html:typography': {
            if (!setting.targetSelector) {
              alert(`Thiếu targetSelector: ${setting.name}`);
            } else {
              const $tagName = $atom.find('h1,h2,h3,h4,h5,h6,p');
              if ($tagName.length > 0) {
                let tagName: string = $tagName.prop('tagName') || '';
                tagName = tagName.toLowerCase();
                Object(mapValue)[setting.name] = tagName;
                $settingsPanel
                  .find(`[name="${setting.name}"]`)
                  .attr('value', tagName)
                  .val(tagName)
                  .trigger('value-change');
              }
            }
            break;
          }
          case 'custom:fixedMode': {
            if (!setting.targetSelector) {
              alert(`Thiếu targetSelector: ${setting.name}`);
            } else {
              const $target = $atom.find(setting.targetSelector);
              let value = '';
              const attrValue = $target.attr(setting.property);
              if (attrValue) {
                value = attrValue;
              }
              if (!value) {
                value = setting.default_value || '';
              }

              Object(mapValue)[setting.name] = value;
              $settingsPanel.find(`[name="${setting.name}"]`).attr('value', value).val(value).trigger('value-change');
            }
            break;
          }
          case 'css:typography': {
            if (!setting.targetSelector) {
              alert(`Thiếu targetSelector: ${setting.name}`);
            } else {
              const targetSelectorAtom = this.getTargetSelectorAtom(setting.targetSelector, $atom);
              this.readLinksValueSeting(
                $atom,
                style,
                setting,
                screenType,
                targetSelectorAtom.targetClass,
                $activePanel,
                targetSelectorAtom.heading,
                mapValue,
              );
            }
            break;
          }
          case 'css:bgImage': {
            const background = HELPER.getImageBg($atom);
            let value = HELPER.getValueFromCSS($atom, style, setting.property, screenType, setting.targetSelector);
            $activePanel.find('input[name="image"]').val(background).attr('value', background).trigger('value-change');
            if (!value) {
              $activePanel
                .find('input[name="image"]')
                .val(background)
                .attr('value', background)
                .trigger('value-change');
              value = setting.default_value || HELPER.getImageBgOption(background);
            }
            Object(mapValue)[setting.name] = value;
            break;
          }
          case 'css:height': {
            if (!setting.targetSelector) {
              alert(`Thiếu targetSelector: ${setting.name}`);
            } else {
              let value = HELPER.getValueFromCSS($atom, style, setting.property, screenType, setting.targetSelector);
              if (!value) {
                value = setting.default_value || '';
              }
              Object(mapValue)[setting.name] = value;
            }
            break;
          }
          case 'custom:condition': {
            if (!setting.condition) {
              alert(`Thiếu condition: ${setting.name}`);
            } else if (!setting.condition.syntax) {
              alert(`Thiếu condition syntax: ${setting.name}`);
            } else {
              let value = setting.condition.fail;
              let syntax = setting.condition.syntax;
              const regex = /#{(.*?)}/gm;
              const matches = syntax.match(regex);
              if (matches && matches.length) {
                for (let i = 0; i < matches.length; i++) {
                  const match = matches[i];
                  let name = match;
                  name = name.replace('#{', '').replace('}', '');
                  syntax = syntax.replace(match, Object(mapValue)[name]);
                }
              }
              if (eval(syntax)) {
                value = setting.condition.success;
              }
              if (setting.name == 'hideOnClient') {
                if (setting.targetSelector) {
                  const $target = $atom.find(setting.targetSelector);
                  const defaultValue = $target.attr('value');
                  if (defaultValue && defaultValue != ' ') {
                    value = '1';
                  } else {
                    value = '0';
                  }
                }
              }
              Object(mapValue)[setting.name] = value;
              $activePanel.find(`[name="${setting.name}"]`).attr('value', value).val(value).trigger('value-change');
            }
            break;
          }
          case 'css:group': {
            if (!setting.targetSelector) {
              alert(`Thiếu targetSelector: ${setting.name}`);
            } else if (!setting.links) {
              alert(`Thiếu links : ${setting.name}`);
            } else {
              const links = setting.links.split('|');
              // const fieldLinks:any =[];
              if (links.length < 2) {
                alert(`Links thiếu stack: ${setting.name}`);
              } else {
                const targetClass = this.getTargetSelectorAtom(setting.targetSelector, $atom).targetClass;
                let value = HELPER.getValueFromCSS($atom, style, setting.property, screenType, targetClass);
                if (value == '') {
                  value = $atom.find(targetClass).css(setting.property);
                }
                if (value !== 'none') {
                  $activePanel
                    .find(`input[name="${setting.name}"]`)
                    .val('1')
                    .attr('value', '1')
                    .trigger('value-change');
                  if (setting.property == 'text-shadow') {
                    const textShadowValue = this.getTextShadowValue($atom.find(targetClass));
                    $activePanel
                      .find('input[name="box-shadow-color"]')
                      .val(HELPER.rgb2hex(textShadowValue.color))
                      .attr('value', HELPER.rgb2hex(textShadowValue.color))
                      .trigger('value-change');
                    $activePanel
                      .find('input[name="box-shadow-blur"]')
                      .val(textShadowValue.blur)
                      .attr('value', textShadowValue.blur)
                      .trigger('value-change');
                    setTimeout(function () {
                      $activePanel
                        .find('input[name="gf-to-shadowXY"]')
                        .val(textShadowValue.y.replace('px', '') + ',' + textShadowValue.x.replace('px', ''))
                        .attr('value', textShadowValue.y.replace('px', '') + ',' + textShadowValue.x.replace('px', ''))
                        .trigger('value-change');
                    }, 100);
                  }
                } else {
                  $activePanel
                    .find(`input[name="${setting.name}"]`)
                    .val('0')
                    .attr('value', '0')
                    .trigger('value-change');
                }
                Object(mapValue)[setting.name] = value;
              }
            }
            break;
          }
          case 'html:hasClass': {
            if (!setting.targetSelector) {
              alert(`Thiếu targetSelector: ${setting.name}`);
            } else {
              const $target = $atom.find(setting.targetSelector);
              const value = $target.hasClass(setting.property) ? 1 : 0;
              Object(mapValue)[setting.name] = value;
              $activePanel.find(`[name="${setting.name}"]`).attr('value', value).val(value).trigger('value-change');
            }
            break;
          }
          case 'html:stretch': {
            if (!setting.targetSelector) {
              alert(`Thiếu targetSelector: ${setting.name}`);
            } else if (!setting.attrStoredSelector) {
              alert(`Thiếu attrStoredSelector: ${setting.name}`);
            } else {
              const $target = $atom.find(setting.attrStoredSelector);
              const currentValue: string = $target.attr('data-stretch-' + screenType) || '';
              Object(mapValue)[setting.name + '_' + screenType] = currentValue;
              $activePanel
                .find(`[name="${setting.name}"]`)
                .attr('value', currentValue)
                .val(currentValue)
                .trigger('value-change');
            }
            break;
          }
          case 'font-style': {
            const targetSeletor = setting.targetSelector as string;
            const $targetSelector = $atom.find(targetSeletor);

            // Italic
            let fontItalic = HELPER.getValueFromCSS(
              $targetSelector,
              style,
              'font-style',
              screenType,
              setting.targetSelector,
            );
            if (fontItalic == '') {
              fontItalic = $targetSelector.css('font-style');
            }
            if (fontItalic != 'italic') {
              fontItalic = '';
            }

            // Bold
            let fontWeight = HELPER.getValueFromCSS(
              $targetSelector,
              style,
              'font-weight',
              screenType,
              setting.targetSelector,
            );
            if (fontWeight == '') {
              fontWeight = $targetSelector.css('font-weight');
            }
            if (fontWeight != 'bold') {
              fontWeight = '';
            }

            // Underline
            let fontUnderline = HELPER.getValueFromCSS(
              $targetSelector,
              style,
              'text-decoration',
              screenType,
              setting.targetSelector,
            );
            if (fontUnderline == '') {
              fontUnderline = $targetSelector.css('text-decoration');
            }
            if (fontUnderline != 'underline') {
              fontUnderline = '';
            }

            const fontStyle = `${fontItalic},${fontWeight},${fontUnderline}`;
            $activePanel.find('#font-style').val(fontStyle).attr('value', fontStyle).trigger('value-change');
            break;
          }
        }

        const settingHandler = SettingHandlerManager.getSingleton(setting);
        if (settingHandler) {
          const result = settingHandler.read({
            screenType,
            $activePanel,
            $atom: $currentAtom,
            atomModel,
          });
          if (result) {
            if (result.value) {
              Object(mapValue)[setting.name] = result.value;
            }
          }
        }
      });

      // end foreach
    }
  }

  /**
   * save all form control to divContentBk
   * @param name unique identifier of control
   * @param value value of control
   * @param $atom Atom DOM
   * @param atomModel Atom data
   * @param $activePanel curent active panel
   */
  saveFormControls(
    name: string,
    value: any,
    $atom: JQuery,
    atomModel: AtomModel,
    $activePanel: JQuery,
    type?: string,
  ): void {
    if (!name) {
      return;
    }
    const _this = this;
    let refreshType = 'all'; // eg: refreshType = "css:js:html" || "css:html" || "css"
    // const renderType = "" // template, style, script, childs
    /** Save settings */

    /** Save style & template */
    let style = atomModel.style;
    const formControlSettings = atomModel.formControlSettings;
    if (formControlSettings && formControlSettings.length > 0) {
      const gfResTool = _this.$gfResTool.data('gfrestool');
      const screenType = gfResTool != undefined ? gfResTool.getCurrentStatus() : 'lg';
      if (type == 'history') {
        formControlSettings.forEach((setting) => {
          value = $activePanel.find(`[name="${setting.name}"]`).val();
          const settingHandler = SettingHandlerManager.getSingleton(setting);
          if (settingHandler) {
            const result = settingHandler?.write(value, {
              screenType,
              $atom,
              atomModel,
              $activePanel,
            });
            if (result) {
              refreshType = result.refreshType;
              style = result.style || style;
            }
          } else {
            const result = this.handleSaveFormControl(setting, screenType, value, $atom, atomModel, $activePanel);
            if (result) {
              refreshType = result.refreshType;
              style = result.style || style;
            }
          }
          switch (refreshType) {
            case 'css': {
              DataManager.setState('changeSetting', { type: refreshType, data: style }, this.currentAtomId);
              break;
            }
            case 'all': {
              DataManager.setState('changeSetting', { type: refreshType }, this.currentAtomId);
              break;
            }
          }
          DataManager.getInstance().notify();
          //----------------------
          HELPER.flagListItemChange($atom, 'settings');
          const $itemContent = $atom.closest('.item-content');
          if ($itemContent && $itemContent.length) {
            HELPER.detectContentChange($itemContent);
          }
        });
        return;
      }
      formControlSettings.forEach((setting) => {
        if (setting.name == name) {
          const settingHandler = SettingHandlerManager.getSingleton(setting);
          if (settingHandler) {
            const result = settingHandler?.write(value, {
              screenType,
              $atom,
              atomModel,
              $activePanel,
            });
            if (result) {
              refreshType = result.refreshType;
              style = result.style || style;
            }
          } else {
            const result = this.handleSaveFormControl(setting, screenType, value, $atom, atomModel, $activePanel);
            if (result) {
              refreshType = result.refreshType;
              style = result.style || style;
            }
          }
        }
      });
    }
    if (atomModel.type == 'module') {
      /** Save data childs */
      this.collectInputForm($activePanel);
    }
    switch (refreshType) {
      case 'css': {
        DataManager.setState('changeSetting', { type: refreshType, data: style }, this.currentAtomId);
        break;
      }
      case 'all': {
        DataManager.setState('changeSetting', { type: refreshType }, this.currentAtomId);
        break;
      }
    }
    DataManager.getInstance().notify();
    //----------------------
    HELPER.flagListItemChange($atom, 'settings');
    const $itemContent = $atom.closest('.item-content');
    if ($itemContent && $itemContent.length) {
      HELPER.detectContentChange($itemContent);
    }
  }

  /**
   * handleSaveFormControl
   * Khởi tạo function để handle switch type 1 nơi
   * Để sau dễ convert
   */
  handleSaveFormControl(
    setting: any,
    screenType: string,
    value: any,
    $atom: JQuery,
    atomModel: AtomModel,
    $activePanel: JQuery,
  ) {
    const atomName = $atom.data('label');
    let style = atomModel.style;
    let refreshType = 'all';
    const formControlSettings: any = atomModel.formControlSettings;
    if (atomModel && atomModel.name == 'Image') {
      const imgWidth: any = $activePanel.find('#width').attr('value');
      const imgHeight: any = $activePanel.find('#height').attr('value');
      const $elemntImg = $atom.find('.elm img');
      const naturalWidth = ($elemntImg.get(0) as any).naturalWidth;
      const naturalHeight = ($elemntImg.get(0) as any).naturalHeight;
      $elemntImg.attr('natural-width', naturalWidth).attr('natural-height', naturalHeight);
      $elemntImg.attr('data-width', imgWidth).attr('data-height', imgHeight);
    }
    if (setting.selfSelector) {
      const selfSelector: string = setting.selfSelector;
      const isUsingSelf: any = selfSelector.split(',')[1];
      if (isUsingSelf == 1) {
        const moduleClass = this.getTargetClass(atomName);
        if (moduleClass != '') {
          $atom = $atom.find(moduleClass);
        }
      }
    }
    if (setting.attrStoredSelector && setting.attrStoredSelector == '.module') {
      let targetSelector = setting.targetSelector as string;
      if (targetSelector.includes(':hover')) {
        targetSelector = targetSelector.split(':hover')[0];
      }
    }
    switch (setting.type) {
      case 'css:typography': {
        if (!setting.targetSelector) {
          alert(`Thiếu targetSelectors: ${setting.name}`);
        } else {
          refreshType = getRefreshType(setting, 'css');
          let deOptions: any = [];
          // let value:any;
          if (setting.options?.length) {
            for (let i = 0; i < setting.options.length; i++) {
              const option = setting.options[i];
              if (option.key == value) {
                value = option.value;
              }
            }
          }
          const targetClass: string = this.getTargetSelectorAtom(setting.targetSelector, $atom).targetClass || '';
          if (setting.links) {
            const $settingsPanel = this.$gfLeftSideBar.find('.settings-panel');
            const links = setting.links.split('|');
            let fieldLinks: any = [];
            fieldLinks = links[0].split(',');
            let linksValue = '';
            fieldLinks.forEach((link: string) => {
              let itemValue: string = $settingsPanel.find(`[name="${link}"]`).attr('value') || '';
              if (link === 'gf-to-shadowXY') {
                let shadowXYArr: any = [];
                if (itemValue) {
                  shadowXYArr = itemValue.split(',');
                } else {
                  shadowXYArr = ['0', '0'];
                }
                for (let i = 0; i < shadowXYArr.length; i++) {
                  if (shadowXYArr[i].indexOf('px') == -1) {
                    shadowXYArr[i] += 'px';
                  }
                }
                itemValue = shadowXYArr.toString().replace(/,/g, ' ');
              } else {
                itemValue = $settingsPanel.find(`[name="${link}"]`).attr('value') || '';
              }
              linksValue += itemValue + ' ';
            });
            linksValue = linksValue.replace(/undefined/g, '');
            try {
              linksValue = jQuery.trim(linksValue);
            } catch (e) {
              /* eslint no-console: ["error", { allow: ["warn", "error", "log"] }] */
              console.log(e);
            }
            deOptions = [
              {
                key: setting.property,
                value: linksValue,
              },
            ];
          } else {
            deOptions = [
              {
                key: setting.property,
                value: value,
              },
            ];
          }
          style = HELPER.designOptions2Style($atom, screenType, style, deOptions, targetClass);
        }
        break;
      }
      case 'css:bgImage': {
        refreshType = getRefreshType(setting, 'css');
        const valueUpdate = value ? HELPER.getImageBgOption(value) : 'none';
        const deOptions = [
          {
            key: setting.property,
            value: valueUpdate,
          },
        ];
        style = HELPER.designOptions2Style($atom, screenType, style, deOptions);
        break;
      }
      case 'css:height': {
        refreshType = getRefreshType(setting, 'css');
        const deOptions = [];
        const height = value;
        let dragPlaceHolder = 50;
        if (height != '' && height != 'auto') {
          deOptions.push(
            {
              key: 'height',
              value: height,
            },
            {
              key: 'padding-top',
              value: parseFloat(height) / 2 - dragPlaceHolder / 2 + 'px',
            },
            {
              key: 'padding-bottom',
              value: '10px',
            },
          );
        } else {
          const paddingTop = HELPER.getValueFromCSS($atom, style, 'padding-top', screenType);
          let paddingBottom = HELPER.getValueFromCSS($atom, style, 'padding-bottom', screenType);

          if (parseInt(paddingBottom) == 10) {
            paddingBottom = paddingTop;

            dragPlaceHolder = 50;

            deOptions.push(
              {
                key: 'height',
                value: 'auto',
              },
              {
                key: 'padding-top',
                value: parseInt(paddingTop) + 'px',
              },
              {
                key: 'padding-bottom',
                value: parseInt(paddingBottom) + 'px',
              },
            );
          }
        }
        style = HELPER.designOptions2Style($atom, screenType, style, deOptions);
        break;
      }
      case 'custom:condition': {
        if (!setting.condition) {
          alert(`Thiếu condition: ${setting.name}`);
        } else if (!setting.condition.syntax) {
          alert(`Thiếu condition syntax: ${setting.name}`);
        } else {
          refreshType = getRefreshType(setting, 'css');
          // support GpEmailField
          if (setting.name == 'hideOnClient') {
            if (setting.targetSelector) {
              let defaultValue = '';
              const $target = $atom.find(setting.targetSelector);
              if (value && value == '1') {
                defaultValue = $activePanel.find('[name="defaultvalue"]').attr('value') || '';
                $target.attr('hide', 'true');
              } else {
                $target.removeAttr('hide');
              }
              $target.attr('value', defaultValue);
            }
          }
          if (setting.links) {
            const links = setting.links.split(',');
            for (let i = 0; i < links.length; i++) {
              // find object in array of objects with field name
              const obj: any = formControlSettings.find((o: any) => o.name === links[i]);
              const targetClass: string = this.getTargetSelectorAtom(obj.targetSelector, $atom).targetClass;
              this.readLinksValueSeting($atom, style, obj, screenType, targetClass, $activePanel);
            }
            if (value == setting.condition.fail) {
              let valueReset = '';
              for (let i = 0; i < links.length; i++) {
                // find object in array of objects with field name
                const obj: any = formControlSettings.find((o: any) => o.name === links[i]);
                const targetClass: string = this.getTargetSelectorAtom(obj.targetSelector, $atom).targetClass;
                if (obj.fieldType == 'color') {
                  valueReset = 'none';
                  $activePanel
                    .find(`[name="${obj.targetSelector}"]`)
                    .val('none')
                    .attr('value', 'none')
                    .trigger('value-change');
                } else {
                  valueReset = '';
                  $activePanel.find(`[name="${obj.targetSelector}"]`).val('').attr('value', '').trigger('value-change');
                }
                const deOptions = [
                  {
                    key: obj.property,
                    value: valueReset,
                  },
                ];
                style = HELPER.designOptions2Style($atom, screenType, style, deOptions, targetClass);
              }
            }
          }
        }
        break;
      }
      case 'html:replace': {
        if (!setting.targetSelector) {
          alert(`Thiếu targetSelector: ${setting.name}`);
        } else if (!setting.replaceData) {
          alert(`Thiếu replaceData: ${setting.name}`);
        } else {
          refreshType = getRefreshType(setting, 'all');
          const $target = $atom.find(setting.targetSelector);
          const replaceData = setting.replaceData || {};
          const replaceType = replaceData.replaceType || '';
          if (replaceType == 'icon') {
            const icon = HELPER.base64UrlDecode(value) || '';
            $target.html(icon);
          }
        }
        break;
      }
      case 'html:select': {
        if (!setting.targetSelector) {
          alert(`Thiếu targetSelector: ${setting.name}`);
        } else {
          refreshType = getRefreshType(setting, 'css');
          const $target = $atom.find(setting.targetSelector);
          let options: any = $activePanel.find(`[name="${setting.name}"]`).attr('value');
          if (options) {
            options = options.split(/\n/);
          }
          let htmlOptions = '';
          const newTxt = [];
          for (let i = 0; i < options.length; i++) {
            const arr = options[i].split('=>');
            if (arr.length == 2) {
              let key = arr[1];
              let value = arr[0];
              if (key != null && key != '') key = key.trim();
              if (value != null && value != '') value = value.replace(/["']/g, '').trim();
              newTxt.push(value + ' => ' + key);
              htmlOptions += '<option value="' + value + '">' + key + '</option>';
            }
          }
          $target.html(htmlOptions);
        }
        break;
      }
      case 'html:typography': {
        const $settingsPanel = this.$gfLeftSideBar.find('.settings-panel');
        if (!setting.targetSelector) {
          alert(`Thiếu targetSelector: ${setting.name}`);
        } else {
          refreshType = getRefreshType(setting, 'css');
          const $tagName = $atom.find('h1,h2,h3,h4,h5,h6,p');
          if ($tagName.length > 0) {
            let tagName: string = $settingsPanel.find(`[name="${setting.name}"]`).attr('value') || '';
            tagName = tagName.toLowerCase();
            // replace old heading tag
            jQuery($tagName).replaceWith(function () {
              return '<' + tagName + '>' + $(this).html() + '</' + tagName + '>';
            });
            if (setting.links) {
              const links = setting.links.split(',');
              for (let i = 0; i < links.length; i++) {
                // find object in array of objects with field name
                const obj: any = formControlSettings.find((o: any) => o.name === links[i]);
                const targetClass = this.getTargetSelectorAtom(obj.targetSelector, $atom).targetClass;
                this.saveLinksValueSetting($atom, style, obj, screenType, targetClass, $activePanel, setting.name);
              }
            }
          }
        }
        break;
      }
      case 'html:hasClass': {
        if (!setting.targetSelector) {
          alert(`Thiếu targetSelector: ${setting.name}`);
        } else {
          refreshType = getRefreshType(setting, 'all');
          const $target = $atom.find(setting.targetSelector);
          if (value == '1') {
            $target.addClass(setting.property);
          } else {
            $target.removeClass(setting.property);
          }
        }
        break;
      }
      case 'html:stretch': {
        if (!setting.targetSelector) {
          alert(`Thiếu targetSelector: ${setting.name}`);
        } else if (!setting.attrStoredSelector) {
          alert(`Thiếu attrStoredSelector: ${setting.name}`);
        } else {
          // const $target = $atom.find(setting.targetSelector);
          // HELPER.setResponsiveAlign(value, $target, screenType);
          const $target = $atom.find(setting.attrStoredSelector);
          $target.attr('data-stretch-' + screenType, value);
          refreshType = getRefreshType(setting, 'css');
        }
        break;
      }
      case 'css:group': {
        if (!setting.targetSelector) {
          alert(`Thiếu targetSelector: ${setting.name}`);
        } else if (!setting.links) {
          alert(`Thiếu links : ${setting.name}`);
        } else {
          const $settingsPanel = this.$gfLeftSideBar.find('.settings-panel');
          const links = setting.links.split('|');
          let fieldLinks: any = [];
          if (links.length < 2) {
            alert(`Links thiếu stack: ${setting.name}`);
          } else {
            refreshType = getRefreshType(setting, 'css');
            fieldLinks = links[0].split(',');
            let linksValue = '';
            const stacked = links[1];
            if (stacked == 'stack') {
              fieldLinks.forEach((link: string) => {
                let itemValue: string = $settingsPanel.find(`[name="${link}"]`).attr('value') || '';
                if (link === 'gf-to-shadowXY') {
                  let shadowXYArr: any = [];
                  if (itemValue) {
                    shadowXYArr = itemValue.split(',');
                  } else {
                    shadowXYArr = ['0', '0'];
                  }
                  for (let i = 0; i < shadowXYArr.length; i++) {
                    if (shadowXYArr[i].indexOf('px') == -1) {
                      shadowXYArr[i] += 'px';
                    }
                  }
                  itemValue = shadowXYArr.toString().replace(/,/g, ' ');
                } else {
                  itemValue = $settingsPanel.find(`[name="${link}"]`).attr('value') || '';
                }
                linksValue += itemValue + ' ';
              });
              linksValue = linksValue.replace(/undefined/g, '');
              try {
                linksValue = jQuery.trim(linksValue);
              } catch (e) {
                console.log(e);
              }
            }
            let deOptions: any = [];
            if (value == '1') {
              deOptions = [
                {
                  key: setting.property,
                  value: linksValue,
                },
              ];
            } else {
              if (setting.property == 'text-shadow') {
                deOptions = [
                  {
                    key: setting.property,
                    value: 'unset',
                  },
                ];
              } else {
                deOptions = [
                  {
                    key: setting.property,
                    value: '',
                  },
                ];
              }
            }
            const targetClass = this.getTargetSelectorAtom(setting.targetSelector, $atom).targetClass;
            style = HELPER.designOptions2Style($atom, screenType, style, deOptions, targetClass);
          }
        }
        break;
      }
      case 'font-style': {
        let fontItalic = '';
        let fontWeight = '';
        let fontUnderline = '';

        const arrayFontStyle = HELPER.trim(value, ',').split(',');
        for (let v = 0; v < arrayFontStyle.length; v++) {
          switch (arrayFontStyle[v]) {
            case 'bold':
              fontWeight = 'bold';
              break;
            case 'italic':
              fontItalic = 'italic';
              break;
            case 'underline':
              fontUnderline = 'underline';
              break;
          }
        }
        const deOptions = [
          {
            key: 'font-weight',
            value: fontWeight,
          },
          {
            key: 'font-style',
            value: fontItalic,
          },
          {
            key: 'text-decoration',
            value: fontUnderline,
          },
        ];

        const targetSelector = setting.targetSelector as string;
        style = HELPER.designOptions2Style($atom, screenType, style, deOptions, targetSelector);
        refreshType = 'css';
        break;
      }
      case 'padding': {
        const deOptions = [
          {
            key: 'padding',
            value: $activePanel.find(`[name="${setting.name}"]`).attr('value'),
          },
        ];
        const targetSelector = setting.targetSelector as string;
        style = HELPER.designOptions2Style($atom, screenType, style, deOptions, targetSelector);
        refreshType = 'css';
        break;
      }
      case 'line-through': {
        const lineThrough = $activePanel.find('#line-through').val();
        let lineThroughColor: any = '';
        let lineThroughPosition: any = '';
        let lineThroughSize: any = '0px';
        lineThroughColor = $activePanel.find('#line-through-color').val();

        lineThroughPosition = $activePanel.find('#line-through-position').val();

        const compareSize: any = $activePanel.find('#compare-size').val();

        if (lineThrough == '1' || lineThrough == 1) {
          if (compareSize) {
            lineThroughSize = parseInt(compareSize) * 0.06;
            if (lineThroughSize <= 1) {
              lineThroughSize = 1;
            }
            lineThroughSize = lineThroughSize + 'px';
          }
        }

        const deOptions = [
          {
            key: 'background',
            value: lineThroughColor,
          },
          {
            key: 'transform',
            value: 'translateY(' + lineThroughPosition + ')',
          },
          {
            key: 'height',
            value: lineThroughSize,
          },
        ];
        const targetSelector = setting.targetSelector as string;
        style = HELPER.designOptions2Style($atom, screenType, style, deOptions, targetSelector);
        refreshType = 'css';
        break;
      }
    }
    return {
      refreshType: refreshType,
      style: style,
    };
  }

  /**
   * get all current default_value at LeftSidebar
   * @returns settings field collection
   */
  collectInputForm($settingsPanel: JQuery): any {
    const _this = this;
    const dataSetting = DataManager.getDataSetting(this.currentAtomId);
    const settings = this.getDataBkSettingByKey(base._settings, dataSetting.atom.type, dataSetting.atom.id);
    let fields: any = [];
    if (!jQuery.isEmptyObject(settings)) {
      try {
        fields = JSON.parse(settings);
      } catch (err) {
        return err;
      }
    } else {
      return;
    }

    jQuery.each(fields, function (key, val) {
      if (val.type == 'group' && val.items) {
        // Type group
        for (let h = 0; h < val.items.length; h++) {
          const _selfname = val.items[h].name;
          const _selfnewVal = _this.convertAndReturnNewValue($settingsPanel, _selfname, dataSetting);
          if (typeof fields[key].items[h]['default_value'] != 'undefined') {
            fields[key].items[h]['default_value'] = _selfnewVal;
          }

          //đoạn này khả năng lỗi. Vì không có advanced trong items của group thay bằng đoạn bên dưới chính xác hơn
          if (fields[key].items[h].advanced != undefined && fields[key].items[h].advanced.length > 0) {
            for (let i = 0; i < fields[key].items[h].advanced.length; i++) {
              const _selfname = fields[key].items[h].advanced[i].name;
              const _selfnewVal = $settingsPanel.find('[name="' + _selfname + '"]').attr('value');

              if (typeof fields[key].items[h].advanced[i]['default_value'] != 'undefined') {
                fields[key].items[h].advanced[i]['default_value'] = _selfnewVal;
              }
            }
          }
        }

        if (fields[key].advanced != undefined && fields[key].advanced.length > 0) {
          for (let i = 0; i < fields[key].advanced.length; i++) {
            const _selfname = fields[key].advanced[i].name;
            const _selfnewVal = $settingsPanel.find('[name="' + _selfname + '"]').attr('value');

            if (typeof fields[key].advanced[i]['default_value'] != 'undefined') {
              fields[key].advanced[i]['default_value'] = _selfnewVal;
            }
          }
        }
      } else {
        // Type default
        const _selfname = val.name;
        const _selfnewVal = _this.convertAndReturnNewValue($settingsPanel, _selfname, dataSetting);

        if (typeof fields[key]['default_value'] != 'undefined') {
          fields[key]['default_value'] = _selfnewVal;
        }

        if (fields[key].advanced != undefined && fields[key].advanced.length > 0) {
          for (let i = 0; i < fields[key].advanced.length; i++) {
            const _selfname = fields[key].advanced[i].name;
            const _selfnewVal = $settingsPanel.find('[name="' + _selfname + '"]').attr('value');

            if (typeof fields[key].advanced[i]['default_value'] != 'undefined') {
              fields[key].advanced[i]['default_value'] = _selfnewVal;
            }
          }
        }
      }
    });
    DataManager.setState(
      'setting',
      {
        changed: true,
        data: fields,
        tab: dataSetting.tab,
        drag: dataSetting.drag,
        capture: dataSetting.capture,
        forceOpen: dataSetting.forceOpen,
      },
      this.currentAtomId,
    );
    DataManager.getInstance().notify();
    return fields;
  }
  /**
   * save all form control to divContentBk
   * @param type loại Atom(Row, Element, Module)
   * @param mdSettings khung template của setting Atom
   * @param atomId Atom ID (number)
   * @param panel md-module hoặc md-...
   * @param status Atom loại 'dynamic' hoặc gì đó
   * @returns Jquery object
   */
  showMDSettingsPanel(type: string, mdSettings: string, atomId: string, panel?: string, status?: string): JQuery {
    const $sidePanels = this.$el.find('.side-panels');
    const index = $sidePanels.find('#md-settings').index();
    this.$gfLeftSideBar.data('gfleftsidebar').gotoTab(index);

    const $mdSettings = this.$gfLeftSideBar.find('#md-settings');
    //   const idNumber = atomId.replace(/m/g, "").replace(/e/g, "").replace(/r/g, "").replace(/-/g, "");
    $mdSettings.attr('data-editting', type).data('edittingID', atomId).html(mdSettings);

    $mdSettings.removeAttr('data-status');
    if (status == 'default' || status == 'dynamic') {
      $mdSettings.attr('data-status', 'dynamic');
    }

    let $panel: JQuery;
    if (panel != undefined) {
      $panel = this.$gfLeftSideBar.find('#' + panel);
    } else {
      $panel = this.$gfLeftSideBar.find('#md-settings');
    }
    const $gryffBody = this.$gfFrame.contents().find('body').first();
    switch (type) {
      case 'module': {
        if ($gryffBody.find(`#${atomId}`).data('gfmodule') != undefined) {
          const $atom = $gryffBody.find(`#${atomId}`);
          const atomIcon = $atom.data('gfmodule').getModuleIcon();
          const atomName = $atom.data('gfmodule').getModuleName();

          const $merTitle = $panel.find('.md-title .mer-title');
          $merTitle.attr('data-label', atomName);
          $merTitle.children('span').html(atomName);
          $merTitle.children('i').removeClass('gpicon-herobanner');
          $merTitle.children('i').addClass(atomIcon);
        }
        break;
      }
      case 'element': {
        if ($gryffBody.find(`#${atomId}`).data('gfelement') != undefined) {
          const $atom = $gryffBody.find(`#${atomId}`);
          const atomIcon = $atom.data('gfelement').getModuleIcon();
          const atomName = $atom.data('gfelement').getModuleName();

          const $merTitle = $panel.find('.md-title .mer-title');
          $merTitle.attr('data-label', atomName);
          $merTitle.children('span').html(atomName);
          $merTitle.children('i').removeClass('gpicon-herobanner');
          $merTitle.children('i').addClass(atomIcon);
        }
        break;
      }
      case 'row': {
        if ($gryffBody.find(`#${atomId}`).data('gfrow') != undefined) {
          const $atom = $gryffBody.find(`#${atomId}`);
          const atomIcon = $atom.data('gfrow').getModuleIcon();
          const atomName = $atom.data('gfrow').getModuleName();

          const $merTitle = $panel.find('.md-title .mer-title');
          $merTitle.attr('data-label', atomName);
          $merTitle.children('span').html(atomName);
          $merTitle.children('i').removeClass('gpicon-herobanner');
          $merTitle.children('i').addClass(atomIcon);
        }
        break;
      }
    }

    $panel
      .find('.md-title')
      .append(
        '<button class="back-to-snippets"><svg fill="#FFFFFF" height="24" viewBox="0 0 24 24" width="24" xmlns="http://www.w3.org/2000/svg"><path d="M0 0h24v24H0z" fill="none"/><path d="M21 11H6.83l3.58-3.59L9 6l-6 6 6 6 1.41-1.41L6.83 13H21z"/></svg></button>',
      );
    const _this = this;
    $panel
      .find('.back-to-snippets')
      .off('click')
      .on('click', function () {
        _this.$gfLeftSideBar.data('gfleftsidebar').gotoTab(0);

        // Add to gfCount
        const extraCount = {};
        if (_this.$gryffEditor && _this.$gryffEditor.data('gfcount') != undefined) {
          _this.$gryffEditor.data('gfcount').add('back-to-snippets-btn', extraCount);
        }
        return false;
      });
    return $panel;
  }

  /**
   * Convert Value After Set To Store
   * @param $settingsPanel
   * @param nameInput
   * @param dataSetting
   * @returns
   */
  convertAndReturnNewValue($settingsPanel: JQuery, nameInput: string, dataSetting: TabSettingsData) {
    const currentValue = $settingsPanel.find('[name="' + nameInput + '"]').attr('value');
    if (!currentValue) return '';
    let newValue = currentValue;
    switch (dataSetting.atom.slug) {
      case 'fera-reviews':
        newValue = this.handleValueFeraReviews($settingsPanel, currentValue, nameInput);
        break;
    }
    return newValue;
  }

  /**
   * Check and Get Container ID
   * @param $settingsPanel
   * @param currentValue
   * @param nameInput
   * @returns newValue: string
   */
  handleValueFeraReviews($settingsPanel: JQuery, currentValue: string, nameInput: string) {
    if (!nameInput.includes('Code')) return currentValue;
    const regex = /data-fera-container=(["'])(.*?)\1/;
    const matchValue = currentValue.match(regex);
    let newValue = currentValue;
    if (matchValue) {
      newValue = matchValue[2];
      $settingsPanel
        .find('[name="' + nameInput + '"]')
        .val(newValue)
        .attr('value', newValue)
        .trigger('value-change');
    } else {
      newValue = this.removeSpecialCharactersInvalid($settingsPanel, currentValue, nameInput);
    }
    return newValue;
  }

  /**
   * Remove Special Characters Invalid
   * @param $settingsPanel
   * @param currentValue
   * @param nameInput
   * @returns
   */
  removeSpecialCharactersInvalid($settingsPanel: JQuery, currentValue: string, nameInput: string) {
    const value = currentValue.replaceAll(/[/\\<>~`''""]/g, '');
    $settingsPanel
      .find('[name="' + nameInput + '"]')
      .val(value)
      .attr('value', value)
      .trigger('value-change');
    return value;
  }

  /**
   * Setup template cho tab
   * @param atomModel object AtomModel
   * @returns html template của tab
   */
  getTemplatePanelSetting(atomModel: AtomModel): string {
    return `
        <div id="md-link">
          <span class="md-title">
            <span class="mer-title">
              <i class="gpicon-herobanner"></i>
              <span>${atomModel.name}</span>
            </span>
          </span>
          <div class="simple-tabs">
            <ul class="simple-tabs-item">
              <li>
                <a href="#">
                <svg width="16" height="16" viewBox="0 0 15.993 15.991">
                  <g> <path d="M5.432,15.991A1.8,1.8,0,0,1,3.63,14.1l.048-.935a.808.808,0,0,0-.763-.847L1.9,12.367a1.763,1.763,0,0,1-1.3-.461,1.8,1.8,0,0,1,0-2.679l.7-.627a.809.809,0,0,0,.057-1.14L.593,6.772a1.806,1.806,0,0,1,1.3-3.141l.934.047a.774.774,0,0,0,.582-.205.8.8,0,0,0,.265-.556L3.63,1.9A1.808,1.808,0,0,1,5.349.006,1.777,1.777,0,0,1,6.77.594l.627.693h0a.8.8,0,0,0,.556.265.77.77,0,0,0,.58-.206L9.227.594a1.806,1.806,0,0,1,3.142,1.3l-.047.933a.8.8,0,0,0,.763.845L14.1,3.629a1.824,1.824,0,0,1,1.3.465,1.806,1.806,0,0,1,0,2.679L14.7,7.4a.809.809,0,0,0-.058,1.14l.752.684a1.8,1.8,0,0,1,.13,2.551,1.777,1.777,0,0,1-1.432.592l-.932-.046a.819.819,0,0,0-.847.762l.048,1.017a1.806,1.806,0,0,1-1.725,1.888A1.845,1.845,0,0,1,9.227,15.4l-.629-.7a.807.807,0,0,0-1.139-.059L6.77,15.4a1.8,1.8,0,0,1-1.251.589ZM2.875,11.32h.087a1.81,1.81,0,0,1,1.715,1.9l-.048.933a.8.8,0,0,0,.263.63.762.762,0,0,0,.58.209.8.8,0,0,0,.558-.262l.63-.694a1.647,1.647,0,0,1,.125-.126,1.811,1.811,0,0,1,2.555.127l.627.693a.831.831,0,0,0,.628.259.8.8,0,0,0,.77-.84l-.048-.931c0-.063,0-.121,0-.181a1.793,1.793,0,0,1,1.9-1.714l.933.047a.814.814,0,0,0,.64-.264.809.809,0,0,0-.058-1.139l-.7-.626A1.744,1.744,0,0,1,13.9,9.21a1.809,1.809,0,0,1,.129-2.553l.7-.627a.806.806,0,0,0,0-1.2.769.769,0,0,0-.581-.207l-.932.047c-.061,0-.121,0-.177,0a1.8,1.8,0,0,1-1.715-1.894l.047-.933a.806.806,0,0,0-.264-.64.806.806,0,0,0-1.139.058l-.632.694a1.816,1.816,0,0,1-.132.131,1.828,1.828,0,0,1-1.3.461,1.792,1.792,0,0,1-1.246-.594l-.627-.691A.791.791,0,0,0,5.4,1a.806.806,0,0,0-.767.842l.044.934c0,.061,0,.125,0,.188a1.805,1.805,0,0,1-1.9,1.708L1.843,4.63a.782.782,0,0,0-.633.264.8.8,0,0,0,.055,1.137l.7.63c.044.041.087.083.127.128a1.811,1.811,0,0,1-.128,2.553l-.695.627a.806.806,0,0,0,0,1.194.845.845,0,0,0,.58.206l.935-.047C2.814,11.32,2.844,11.32,2.875,11.32Z"></path> <path d="M8,11.5A3.5,3.5,0,0,1,5.521,5.523a3.584,3.584,0,0,1,4.95,0h0A3.5,3.5,0,0,1,8,11.5Zm0-6A2.5,2.5,0,0,0,6.228,9.766a2.56,2.56,0,0,0,3.536,0,2.5,2.5,0,0,0,0-3.536h0A2.484,2.484,0,0,0,8,5.5Z"></path> </g>
                </svg>
                Settings
                </a>
              </li>
              <li>
                <a href="#">
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M13.4662 3.32536C10.5197 0.0520773 5.41712 0.455895 2.90566 3.15472C0.382242 5.8664 0.384047 10.2859 2.91657 12.8094C4.2171 14.1053 5.59069 14.7294 6.64923 14.9321C7.1818 15.0341 7.6156 15.026 7.91274 14.9595C8.18689 14.8982 8.25848 14.8118 8.26984 14.7981L8.27107 14.7967L8.27156 14.7963C8.27169 14.7963 8.27136 14.7972 8.27073 14.7989C8.29685 14.7278 8.30156 14.617 8.23715 14.4136C8.18767 14.2573 8.11797 14.1031 8.03395 13.9172C8.00289 13.8485 7.96986 13.7755 7.93518 13.6964C7.71082 13.1847 7.3998 12.3891 7.95274 11.6336C8.4658 10.9326 9.33905 10.7681 10.1463 10.6804C10.4007 10.6528 10.6577 10.6321 10.9184 10.6111C11.5548 10.5598 12.214 10.5066 12.9144 10.347C13.6989 10.1683 14.5849 9.27725 14.8972 7.93974C15.2001 6.64274 14.9415 4.96417 13.4662 3.32536ZM2.1736 2.47349C5.05278 -0.620495 10.8391 -1.08779 14.2095 2.65632C15.8927 4.52621 16.2519 6.53614 15.871 8.16715C15.4996 9.75765 14.4002 11.0341 13.1365 11.3221C12.3611 11.4987 11.5867 11.5613 10.9284 11.6145H10.9284C10.686 11.634 10.4594 11.6523 10.2542 11.6746C9.424 11.7647 8.98676 11.914 8.75971 12.2242C8.57255 12.48 8.61675 12.7606 8.851 13.2948C8.87385 13.3469 8.89918 13.4028 8.92575 13.4615L8.92576 13.4615L8.92577 13.4615C9.01646 13.6618 9.12148 13.8937 9.1905 14.1117C9.2831 14.4041 9.34666 14.7697 9.20951 15.1435C9.03847 15.6095 8.56576 15.8381 8.13113 15.9354C7.66696 16.0393 7.08993 16.0347 6.46112 15.9143C5.19691 15.6722 3.64554 14.9475 2.21072 13.5178C-0.727454 10.5901 -0.693623 5.55462 2.1736 2.47349ZM3.86219 6.01018C4.25792 6.40591 4.25792 7.04752 3.86219 7.44324C3.46646 7.83898 2.82485 7.83898 2.42912 7.44324C2.03339 7.04751 2.03339 6.4059 2.42912 6.01018C2.82485 5.61445 3.46646 5.61445 3.86219 6.01018ZM13.6605 7.44324C14.0563 7.04752 14.0563 6.40591 13.6605 6.01018C13.2648 5.61445 12.6232 5.61445 12.2275 6.01018C11.8317 6.4059 11.8317 7.04751 12.2275 7.44324C12.6232 7.83897 13.2648 7.83897 13.6605 7.44324ZM10.9457 2.64592C11.3414 3.04165 11.3414 3.68326 10.9457 4.07899C10.55 4.47472 9.90835 4.47472 9.51262 4.07899C9.11689 3.68325 9.11689 3.04165 9.51262 2.64592C9.90835 2.25019 10.55 2.25019 10.9457 2.64592ZM6.62049 4.07899C7.01622 3.68326 7.01622 3.04165 6.62049 2.64592C6.22476 2.25019 5.58316 2.25019 5.18742 2.64592C4.79169 3.04165 4.79169 3.68325 5.18742 4.07899C5.58315 4.47472 6.22476 4.47472 6.62049 4.07899Z"></path>
                  </svg>
                  Design
                </a>
              </li>
            </ul>
            <div class="md-body settings-panel">

            </div>
            <div class="md-body design-panel">
            </div>
            <textarea class="txtDataHidden inptxt gf-hide"></textarea>
            <textarea class="txtStyleHidden inptxt gf-hide"></textarea>
          </div>
          <div class="md-footer">
            <button class="btnSave">APPLY</button>
            <button class="btnCancel">CANCEL</button>
          </div>
          <div class="md-controls">
            <button class="btnClose"><i class="fa fa-times" aria-hidden="true"></i></button>
          </div>
        </div>
      `;
  }
  /**
   * Setup template cho tab
   * @param atomId atomId
   * @returns html template của tab
   */
  highlightCounter(atomId: string, type: string): number {
    app.$store.commit('sidebar/SET_SIDE_BAR_SETTING_ELEMENT_ID', {
      merId: atomId,
      type: type,
    });
    const $mdSettings = this.$gfLeftSideBar.find('#md-settings');
    let _counter = $mdSettings.attr('hightlight-counter');
    if (!_counter) {
      _counter = 'id,number';
    }
    const $arrayCounter = _counter.split(',');
    const _id = $arrayCounter[0];
    let number: number = $arrayCounter[1] ? parseInt($arrayCounter[1]) : 0;

    if (atomId != _id) {
      number = 1;
    } else {
      number++;
    }
    $mdSettings.attr('hightlight-counter', atomId + ',' + number);
    return number;
  }
  /**
   * getTargetTypography
   * @param targetSelector string selector from seting of Atom
   * @param $atom Atom jquery DOM
   * @returns targetClass, $heading
   */
  getTargetSelectorAtom(targetSelector: string, $atom: JQuery): any {
    let targetclass = targetSelector;
    let $heading: any;
    // Dành riêng cho heading có dạng: .text-edit+heading
    if (targetSelector && targetSelector.indexOf('+heading') !== -1) {
      let newTargetclass = targetclass.split('+')[0];
      $heading = $atom.find(newTargetclass).children('h1,h2,h3,h4,h5,h6,p');
      if ($heading.length > 0) {
        switch ($heading.prop('tagName').toLowerCase()) {
          case 'h1':
            newTargetclass += ' h1';
            break;

          case 'h2':
            newTargetclass += ' h2';
            break;

          case 'h3':
            newTargetclass += ' h3';
            break;

          case 'h4':
            newTargetclass += ' h4';
            break;

          case 'h5':
            newTargetclass += ' h5';
            break;

          case 'h6':
            newTargetclass += ' h6';
            break;

          case 'p':
            newTargetclass += ' p';
            break;
        }
      } else {
        $heading = $atom.find(newTargetclass);
      }
      targetclass = newTargetclass;
    }
    return {
      targetClass: targetclass,
      heading: $heading,
    };
  }
  /**
   * saveLinksValueSetting
   */
  saveLinksValueSetting(
    $atom: JQuery,
    style: string,
    setting: FormControlSetting,
    screenType: string,
    targetClass: string,
    $activePanel: JQuery,
    name: string,
  ) {
    let deOptions: any;
    let shadowXY: any = $activePanel.find('input[name="gf-to-shadowXY"]').val();
    let textShadowValue = '';
    if (shadowXY) {
      shadowXY = shadowXY.split(',');
    } else {
      shadowXY = ['0', '0'];
    }
    textShadowValue =
      $activePanel.find('input[name="box-shadow-enable"]').val() == '1'
        ? (shadowXY.length >= 2 ? shadowXY[0] : 0) +
          'px ' +
          (shadowXY.length >= 2 ? shadowXY[1] : 0) +
          'px ' +
          $activePanel.find('input[name="box-shadow-blur"]').val() +
          ' ' +
          $activePanel.find('input[name="box-shadow-color"]').val()
        : '';
    const inputName = name;
    if (
      inputName &&
      (inputName == 'box-shadow-enable' ||
        inputName == 'box-shadow-color' ||
        inputName == 'gf-to-shadowXY' ||
        inputName == 'box-shadow-angle' ||
        inputName == 'box-shadow-distance' ||
        inputName == 'box-shadow-blur')
    ) {
      deOptions = [
        {
          key: 'text-shadow',
          value: textShadowValue,
        },
      ];
    } else {
      deOptions = [
        {
          key: setting.property,
          value: $activePanel.find(`[name="${setting.name}"]`).val(),
        },
      ];
    }
    style = HELPER.designOptions2Style($atom, screenType, style, deOptions, targetClass);
    return style;
  }
  /**
   * get style of atom to fill in to left side bar fields
   * @param $atom Atom Dom
   * @param style Atom Style
   * @param setting Atom Setting
   * @param screenType lg/md/sm/xs
   * @param targetClass target selector
   * @param $activePanel active panel of left sidebar
   * @param mapValue tmp map value object
   */
  readLinksValueSeting(
    $atom: JQuery,
    style: string,
    setting: FormControlSetting,
    screenType: string,
    targetClass: string,
    $activePanel: JQuery,
    $heading?: JQuery,
    mapValue?: any,
  ): void {
    let value: any;
    value = HELPER.getValueFromCSS($atom, style, setting.property, screenType, targetClass);

    // vì mặc địch là sẽ ko lấy font-family từ css
    if (value == '' && setting.property && setting.property != 'font-family') {
      value = $atom.find(targetClass).css(setting.property);
    }
    if (!value || value == 'none') {
      value = setting.default_value || '';
    }
    if (setting.name == 'textTransform' && value == '') {
      value = $heading?.css('text-transform');
    }
    if (value != '' || !value) {
      // convert px to em
      if (setting.name == 'line-height' && setting.unit == 'em' && value.indexOf('px') !== -1) {
        const fontSize = $atom.find(targetClass).css('font-size');
        try {
          const val = parseFloat(value) / parseFloat(fontSize);
          value = val.toFixed(2) + 'em';
        } catch (e) {
          console.log('fail to convert px to em');
        }
      }

      if (setting.name == 'box-shadow-blur' && value !== setting.default_value) {
        const blur = this.getTextShadowValue($atom.find(targetClass)).blur;
        value = blur;
      }
    }
    if (setting.options?.length) {
      for (let i = 0; i < setting.options.length; i++) {
        const option = setting.options[i];
        if (option.value == value) {
          value = option.key;
        }
      }
    }
    if (setting.fieldType == 'color') {
      let color: string = value;
      if (value == 'none') {
        value = setting.default_value;
      } else {
        if (setting.name == 'box-shadow-color') {
          color = this.getTextShadowValue($atom.find(targetClass)).color;
        }
        value = HELPER.rgb2hex(color);
      }
    }
    if (setting.fieldType && setting.fieldType == 'ShadowAngle') {
      let shadowXYArr: any = [];
      if (value && value != 'none') {
        shadowXYArr = value.split(',');
      } else {
        shadowXYArr = ['0', '0'];
      }
      for (let i = 0; i < shadowXYArr.length; i++) {
        shadowXYArr[i] += 'px';
      }
      value = shadowXYArr.toString().replace(/,/g, ' ');
    }
    $activePanel.find(`[name="${setting.name}"]`).attr('value', value).val(value).trigger('value-change');
    if (mapValue) {
      Object(mapValue)[setting.name] = value;
    }
  }
  /**
   * get all value of text-shadow property
   * @param $el Element DOM
   * @returns object {color, y, x, blur}
   */
  getTextShadowValue($el: JQuery): ShadowValue {
    const result: any = $el.css('text-shadow').split(' ');
    // result => ['rgb(30, 43, 2)', '-4px', '11px', '8px']
    const color = result[0] + result[1] + result[2];
    const y = result[3];
    const x = result[4];
    const blur = result[5];
    let returnVal: ShadowValue = {
      color: '',
      y: '',
      x: '',
      blur: '',
    };
    if (result && result != 'none') {
      returnVal = {
        color: color,
        y: y,
        x: x,
        blur: blur,
      };
    }
    return returnVal;
  }
  /**
   * getTargetClass
   * @param label :string
   * @returns string
   */
  getTargetClass(label?: string): string {
    // const $gryffBody = this.$gfFrame.contents().find("body").first();
    // label = $atom.data("gfmodule").getModuleName()
    let targetClass = '';
    const moduleName = label;
    for (let i = 0; i < moduleInfo.length; i++) {
      if (moduleName == moduleInfo[i]['name']) {
        targetClass = '.' + jQuery.trim(moduleInfo[i]['class']);
        break;
      }
    }
    return targetClass;
  }

  /**
   * get DataSnippet store vuex
   * @returns  Data Bk Setting
   */
  getDataBkSettingByKey(keyType: string, atomType: string, idBk = ''): any {
    const keySetting = `${keyType}-${atomType}-${idBk}`;
    const $dataBkSetting = (app as any).$store.getters['editor/GET_DATA_SETTING_BY_KEY'](keySetting);
    return $dataBkSetting;
  }
}
